import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersJhbPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Painters JHB | Professional Painting Services Johannesburg | paintersjhb.co.za',
    description: 'Expert painting services in JHB with 20+ years experience. Residential, commercial, and industrial painting solutions. Free quotes, quality workmanship, affordable rates. Serving all Johannesburg suburbs.',
    keywords: 'painters JHB, painting services JHB, house painters Johannesburg, commercial painters JHB, residential painting Johannesburg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters JHB',
          description: 'Professional painting services in JHB with over 20 years of experience',
          address: 'Johannesburg, Gauteng',
          priceRange: '$$',
          serviceArea: ['Johannesburg', 'Sandton', 'Rosebank', 'Randburg', 'Fourways']
        }),
        generateServiceSchema({
          name: 'Quality Painting Services',
          description: 'Residential, commercial, and industrial painting throughout Johannesburg',
          provider: 'Painters JHB',
          areaServed: ['Johannesburg', 'Sandton', 'Rosebank'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Industrial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-paint-brush-line text-2xl text-[#e9f638]"></i>
              <span className="text-xl font-bold">Cape Town Painters</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get a Quote</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e7f90386b181b90f34d1c9ffe7edfb9a.jpg"
            alt="Painters JHB - Professional Painting Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Painters JHB</h1>
          <p className="text-xl md:text-2xl mb-6">Quality Painting Services Throughout Johannesburg</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://paintersjhb.co.za" target="_blank" rel="noopener noreferrer" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Visit Website</a>
            <Link to="/get-a-quote" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
          </div>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#e9f638] py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-black">20+</div>
              <div className="text-black font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">4500+</div>
              <div className="text-black font-medium">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">97%</div>
              <div className="text-black font-medium">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">100%</div>
              <div className="text-black font-medium">Quality Guarantee</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">About Painters JHB</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters JHB</strong> has been delivering exceptional painting services to Johannesburg residents and businesses for over 20 years. Our reputation for quality workmanship, reliability, and customer satisfaction has made us one of the most trusted painting companies in the greater Johannesburg area. We pride ourselves on transforming spaces with precision, care, and attention to detail that sets us apart from the competition.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Journey and Experience</h3>
              <p className="text-lg leading-relaxed mb-6">
                Since our establishment in 2004, <strong>Painters JHB</strong> has grown from a small local operation to a comprehensive painting service provider serving all areas of Johannesburg and surrounding suburbs. Our two decades of experience have equipped us with the knowledge, skills, and expertise to handle any painting project, regardless of size or complexity. We've successfully completed over 4,500 projects, ranging from small residential touch-ups to large-scale commercial renovations and industrial facilities.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our team consists of highly trained and experienced painters who are passionate about their craft. Each member undergoes continuous training to stay updated with the latest painting techniques, safety standards, and industry best practices. This commitment to professional development ensures that every project we undertake meets the highest standards of quality and craftsmanship. We believe that our people are our greatest asset, and their dedication to excellence is reflected in every job we complete.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">What Makes Painters JHB Different</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters JHB</strong>, we understand that painting is more than just applying color to walls. It's about creating environments that inspire, comfort, and reflect the personality and values of our clients. Our approach is holistic and client-centered, beginning with a thorough consultation to understand your vision, requirements, and budget. We then develop a customized painting plan that addresses your specific needs while offering expert recommendations based on our extensive experience.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Quality is at the heart of everything we do. We use only premium paints and materials from trusted manufacturers, ensuring durability, excellent coverage, and beautiful finishes that stand the test of time. Our meticulous preparation process includes thorough surface cleaning, repair of cracks and imperfections, proper priming, and careful protection of surrounding areas. We never compromise on preparation because we know it's the foundation of a successful painting project.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Comprehensive Painting Services</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters JHB</strong> offers a complete range of painting services designed to meet the diverse needs of our residential, commercial, and industrial clients. Our residential services include interior painting, exterior painting, ceiling painting, trim and molding work, cabinet refinishing, wallpaper installation and removal, and decorative finishes. We work closely with homeowners to create beautiful, personalized spaces that enhance their quality of life and increase property value.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                For commercial clients, we provide professional painting solutions that enhance brand image, create positive impressions, and maintain professional appearances. Our commercial services include office painting, retail space painting, restaurant and hospitality painting, medical facility painting, educational institution painting, and building exterior painting. We understand the importance of minimizing disruption to business operations and work efficiently to complete projects on time and within budget.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our industrial painting services are designed to meet the unique challenges of manufacturing facilities, warehouses, and industrial complexes. We offer specialized coatings, floor painting, equipment painting, safety line marking, and protective coatings that withstand harsh industrial environments. Our team is trained in industrial safety protocols and works to ensure minimal disruption to your operations while delivering durable, high-quality results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Premium Materials and Eco-Friendly Options</h3>
              <p className="text-lg leading-relaxed mb-6">
                We believe that exceptional results require exceptional materials. <strong>Painters JHB</strong> partners with leading paint manufacturers to source premium products that deliver superior performance, durability, and aesthetics. Our extensive product selection includes options for every application, from high-traffic commercial spaces to delicate residential interiors. We can guide you through the selection process, helping you choose the right products based on your specific needs, environmental conditions, and budget considerations.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Environmental responsibility is a core value at <strong>Painters JHB</strong>. We offer a comprehensive range of eco-friendly painting options, including low-VOC and zero-VOC paints that minimize indoor air pollution and environmental impact. Our sustainable practices extend beyond product selection to include proper waste management, efficient application techniques that reduce material waste, and recycling of paint containers and materials whenever possible. We're committed to protecting the environment while delivering exceptional painting results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Proven Process</h3>
              <p className="text-lg leading-relaxed mb-6">
                Every project at <strong>Painters JHB</strong> follows a systematic process designed to ensure quality results and complete customer satisfaction. We begin with a detailed consultation and site assessment, where we discuss your vision, evaluate the scope of work, and identify any special requirements or challenges. This initial phase is crucial for developing an accurate quote and realistic timeline for your project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our preparation phase is thorough and comprehensive. We clean all surfaces to remove dirt, grease, and contaminants, repair cracks, holes, and damaged areas, sand surfaces to ensure proper paint adhesion, apply appropriate primers, and protect floors, furniture, and fixtures with quality drop cloths and protective materials. This meticulous preparation ensures optimal paint performance and beautiful, long-lasting results.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                During the painting phase, our skilled craftsmen apply paint using proven techniques that ensure even coverage, smooth finishes, and clean, precise lines. We maintain a clean, organized work environment and take care to minimize disruption to your daily routine or business operations. Our attention to detail extends to every aspect of the work, from cutting in around trim and fixtures to achieving perfectly smooth roller application and ensuring consistent color throughout the project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Upon completion, we conduct a thorough final inspection with you to ensure every detail meets your expectations. We perform touch-ups as needed, remove all protective materials, clean the work area thoroughly, and dispose of waste responsibly. We don't consider a project complete until you're completely satisfied with the results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Service Coverage Across Johannesburg</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters JHB</strong> proudly serves all areas of Johannesburg and surrounding suburbs. Our comprehensive service area includes Sandton, Rosebank, Randburg, Fourways, Midrand, Roodepoort, Soweto, Alexandra, Bryanston, Morningside, Parktown, Houghton, Melrose, Illovo, Hyde Park, Craighall, Parkhurst, Greenside, Northcliff, Linden, Emmarentia, Auckland Park, Westdene, Melville, Norwood, Orange Grove, Cyrildene, Bedfordview, Edenvale, Kempton Park, Boksburg, Benoni, Germiston, Alberton, Johannesburg CBD, and many other areas throughout Greater Johannesburg and the East Rand.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Customer Satisfaction and Guarantees</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters JHB</strong>, your satisfaction is our ultimate goal. We stand behind the quality of our work with comprehensive warranties and guarantees. Our 97% customer satisfaction rating reflects our commitment to excellence and our dedication to exceeding client expectations on every project. We maintain open communication throughout the project, keeping you informed of progress, addressing concerns promptly, and ensuring the final result meets or exceeds your expectations.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We understand that inviting contractors into your home or business requires trust. That's why we maintain the highest standards of professionalism, reliability, and integrity in all our interactions. Our team members are courteous, respectful, and committed to providing a positive experience from initial consultation through project completion. We treat every property as if it were our own, taking care to protect your belongings and maintain a clean, safe work environment.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Competitive Pricing and Value</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters JHB</strong> offers competitive pricing without compromising on quality. We provide detailed, transparent quotes that clearly outline all costs, so you know exactly what to expect. There are no hidden fees or surprise charges. Our pricing reflects the quality of materials we use, the expertise of our team, and the comprehensive service we provide. We believe in delivering exceptional value by combining quality workmanship, premium materials, and professional service at fair, competitive rates.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Get Started with Your Project Today</h3>
              <p className="text-lg leading-relaxed mb-6">
                Ready to transform your space with professional painting services? Contact <strong>Painters JHB</strong> today for a free, no-obligation quote. Our friendly team is ready to discuss your project, answer your questions, and provide expert guidance on all your painting needs. With over 20 years of experience, thousands of satisfied customers, and a commitment to excellence, we're the painting partner you can trust to deliver outstanding results every time.
              </p>

              <div className="bg-[#e9f638] p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <p className="flex items-center text-lg">
                    <i className="ri-global-line text-2xl mr-3"></i>
                    <a href="https://paintersjhb.co.za" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">paintersjhb.co.za</a>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-map-pin-line text-2xl mr-3"></i>
                    <span className="font-medium">Serving all Johannesburg areas</span>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-time-line text-2xl mr-3"></i>
                    <span className="font-medium">Monday - Saturday: 7:00 AM - 6:00 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Painting Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20home%20interior%20painting%20fresh%20painted%20walls%20living%20room%20elegant%20furniture%20natural%20light%20professional%20finish%20simple%20background&width=400&height=300&seq=service-residential-jhb&orientation=landscape"
                    alt="Residential Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Residential Painting</h3>
                  <p className="text-gray-700 mb-4">Complete interior and exterior painting for homes, including color consultation, surface preparation, and premium finishes.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Interior wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Exterior house painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Ceiling painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Cabinet refinishing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20commercial%20office%20interior%20painting%20professional%20painters%20working%20business%20environment%20clean%20organized%20simple%20background&width=400&height=300&seq=service-commercial-jhb&orientation=landscape"
                    alt="Commercial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Commercial Painting</h3>
                  <p className="text-gray-700 mb-4">Professional painting for businesses, offices, retail spaces, and commercial properties with minimal disruption.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Office painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Retail space painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Restaurant painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building exteriors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=industrial%20warehouse%20facility%20painting%20large%20scale%20commercial%20painting%20equipment%20professional%20painters%20safety%20gear%20simple%20background&width=400&height=300&seq=service-industrial-jhb&orientation=landscape"
                    alt="Industrial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Industrial Painting</h3>
                  <p className="text-gray-700 mb-4">Specialized coating and painting for industrial facilities, warehouses, and manufacturing plants.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Warehouse painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Factory floor coating</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Equipment painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Safety line marking</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=decorative%20wall%20painting%20texture%20finish%20special%20effects%20artistic%20painting%20elegant%20interior%20design%20simple%20background&width=400&height=300&seq=service-decorative-jhb&orientation=landscape"
                    alt="Decorative Finishes"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Decorative Finishes</h3>
                  <p className="text-gray-700 mb-4">Custom decorative painting techniques including faux finishes, textures, and specialty coatings.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Faux finishes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Textured walls</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Venetian plaster</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Metallic finishes</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=exterior%20building%20painting%20scaffolding%20professional%20painters%20working%20facade%20weather%20protection%20coating%20simple%20background&width=400&height=300&seq=service-exterior-jhb&orientation=landscape"
                    alt="Exterior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-gray-700 mb-4">Weather-resistant exterior painting to protect and beautify your property's exterior surfaces.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building facades</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Deck and fence staining</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Waterproofing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=color%20consultation%20paint%20samples%20swatches%20interior%20design%20planning%20professional%20advice%20simple%20background&width=400&height=300&seq=service-consultation-jhb&orientation=landscape"
                    alt="Color Consultation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Color Consultation</h3>
                  <p className="text-gray-700 mb-4">Expert color consultation to help you choose the perfect colors and finishes for your space.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Color scheme design</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Sample testing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Finish recommendations</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trend guidance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Work Gallery</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautifully%20painted%20modern%20living%20room%20interior%20fresh%20white%20walls%20elegant%20furniture%20natural%20lighting%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-jhb-1&orientation=portrait"
                  alt="Residential Interior Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20commercial%20office%20space%20freshly%20painted%20walls%20professional%20finish%20clean%20lines%20business%20environment%20simple%20background&width=400&height=500&seq=gallery-jhb-2&orientation=portrait"
                  alt="Commercial Office Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20house%20exterior%20freshly%20painted%20facade%20modern%20architecture%20professional%20painting%20finish%20sunny%20day%20simple%20background&width=400&height=500&seq=gallery-jhb-3&orientation=portrait"
                  alt="Exterior House Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20interior%20painting%20soft%20colors%20professional%20finish%20comfortable%20atmosphere%20modern%20design%20simple%20background&width=400&height=500&seq=gallery-jhb-4&orientation=portrait"
                  alt="Bedroom Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20retail%20store%20interior%20freshly%20painted%20walls%20professional%20commercial%20finish%20bright%20lighting%20shopping%20environment%20simple%20background&width=400&height=500&seq=gallery-jhb-5&orientation=portrait"
                  alt="Retail Space Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=industrial%20warehouse%20interior%20painted%20walls%20professional%20coating%20clean%20finish%20large%20space%20simple%20background&width=400&height=500&seq=gallery-jhb-6&orientation=portrait"
                  alt="Industrial Facility Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Client Reviews</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters JHB exceeded our expectations! They painted our entire house in Sandton and the results are stunning. The team was professional, efficient, and very respectful of our property. Highly recommend their services!"</p>
                <p className="font-semibold">- Lisa R., Sandton</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We hired Painters JHB for our office in Rosebank and couldn't be happier. They worked around our schedule, completed the job on time, and the quality is exceptional. Great value for money!"</p>
                <p className="font-semibold">- James M., Rosebank</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Excellent service from start to finish. The exterior painting of our home in Fourways looks amazing. The team was punctual, professional, and cleaned up thoroughly after the job. Will definitely use them again!"</p>
                <p className="font-semibold">- Peter S., Fourways</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters JHB did a fantastic job on our apartment in Randburg. Their color consultation service was invaluable in helping us choose the perfect colors. The painters were skilled, friendly, and very tidy. Highly recommended!"</p>
                <p className="font-semibold">- Amanda T., Randburg</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Outstanding work by Painters JHB! They painted our retail store in Midrand and the transformation is incredible. Professional, reliable, and the quality is top-notch. Best painting company we've worked with!"</p>
                <p className="font-semibold">- Mark D., Midrand</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Space?</h2>
          <p className="text-xl mb-8">Get a free quote from Painters JHB today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
            <a href="https://paintersjhb.co.za" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Visit Website</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#e9f638] text-black py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cape Town Painters</h3>
              <p className="text-sm">Your trusted directory for professional painting services across South Africa.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/get-a-quote" className="hover:underline">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Residential Painting</li>
                <li>Commercial Painting</li>
                <li>Industrial Painting</li>
                <li>Decorative Finishes</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Cape Town, South Africa
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@painters-cape-town.co.za
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-black/20 pt-8 text-center text-sm">
            <p>&copy; 2024 Cape Town Painters Directory. All rights reserved.</p>
            <div className="mt-2 space-x-4">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Powered by leaderr</a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
